import { RefObject } from 'react';
import { AlgoliaHit } from 'instantsearch.js';
import { IParsedImage, TImage, IVideoPimResponsive, ICmsLink, IInfoCardWithMedia } from '@components/typescript';
import { TResponsiveType } from '@features/responsive/typescript';
import { IProductSwatch, ISwatch } from '@typescript/index';
import { ILocation, IPointOfDeliveryContact, IPointOfXGallery } from '@features/checkout/typescript';
import { ParsedUrlQuery } from 'querystring';
export interface IProductPriceObject {
  currency: string;
  from: boolean;
  value: number;
  priceList: string;
}
export interface IProductPricesObject {
  basePrice: IProductPriceObject;
  currentPrice: IProductPriceObject;
  omnibusPrice: IProductPriceObject;
  priceUnit: string;
  from: boolean;
}
export interface IProductRapPriceListPriceObject {
  amount: number;
  tax_rate: string;
}
export interface IProductRapPriceListPrice {
  net: IProductRapPriceListPriceObject;
  gross: IProductRapPriceListPriceObject;
  asembly: IProductRapPriceListPriceObject;
}
export interface IProductRapPriceList {
  product_id: number;
  expiry_date: string;
  currency: string;
  from?: boolean;
  base: IProductRapPriceListPrice;
  current: IProductRapPriceListPrice;
  omnibus: IProductRapPriceListPrice;
  base_order: IProductRapPriceListPrice;
  current_order: IProductRapPriceListPrice;
  omnibus_order: IProductRapPriceListPrice;
}
export interface IProductRapOffer {
  product: {
    id: string;
    price_unit: string;
    order_unit: string;
    multiplication: number;
  };
  prices: Record<string, IProductRapPriceList>;
}
export interface IProductRapObject {
  availabilities: unknown;
  prices: Record<string, IProductRapOffer>;
}
export type IProductFileGroupType = 'IMAGE' | 'VIDEO';
export type IProductFileImageType = 'PRODUCT_MAIN' | 'PRODUCT_ARRANGEMENT' | 'PRODUCT_LIST_HOVER' | 'PRODUCT_VIDEO' | 'PRODUCT_MAIN_WITH_SHADOW' | 'PRODUCT_VIDEO_PACKSHOT' | 'PRODUCT_LIST_VIDEO' | 'PRODUCT_LIST_HOVER_OPENED_PACKSHOT';
export interface IProductFileData {
  base?: string;
  group?: 'PHOTO' | 'VIDEO';
}
export interface IThumbnailSrcSet {
  '1x'?: string;
  '2x'?: string;
}
export interface IThumbnailFormats {
  webp?: IThumbnailSrcSet;
  avif?: IThumbnailSrcSet;
  jpg?: IThumbnailSrcSet;
}
export interface IProductFileObject {
  code: IProductFileImageType;
  type: IProductFileGroupType;
  url: string;
  file?: IProductFileData;
  thumbnails?: Record<string, IThumbnailFormats>;
}
export interface IProductPackshots {
  mainShadow: IProductFileObject[];
  mainVideo: IProductFileObject[];
  main: IProductFileObject[];
  arrangement: IProductFileObject[];
  hover: IProductFileObject[];
  hoverOpened: IProductFileObject[];
  hoverVideo: IProductFileObject[];
}
export type TProductFlagsKey = 'PUBLICATION' | 'PUBLICATION_TEST' | 'ASSEMBLY' | 'NEW' | 'NEW_TIL' | 'SHOP_ENABLE' | 'BEHAVIOUR_DELIVERY' | 'HOT_SALE' | 'HOT_SALE_TIL' | 'SURPRISE' | 'GOOD_END' | 'BESTSELLER' | 'BLACK_FRIDAY' | 'FAST_SHIPPING' | 'PROMOTION';
export type TProductFlagObject = {
  key: TProductFlagsKey;
  name: string;
  value: boolean | null | string;
};
export interface IAlgoliaProductFlagFeature {
  name: string;
  value: string | boolean;
}
export interface IAlgoliaProductFlagObject {
  name: string;
  features: IAlgoliaProductFlagFeature[];
}
export interface IAlgoliaProductFeature {
  feature_key: string;
  feature_value: string;
  feature_name: string;
  feature_image_url: string;
  feature_value_descriptive: string;
  link: {
    product_id: number;
  };
}
export interface IProductManufacturerObject {
  id: number;
  name: string;
}
export interface IProductColor {
  value: string;
  featureValueDescriptive: string;
  featureValue: string;
  featureName: string;
  featureKey: string;
  featureImageUrl?: string;
}
export interface IProductFeaturedColor {
  code: string;
  image: string;
  name: string;
  hex: string;
}
export interface IProductColorSwatch {
  totalCount: number;
  featured: IProductSwatch[];
}
export interface IArrangementProduct {
  id: number;
  name: string;
  image: TImage;
  imageWithBlur: IParsedImage;
  video: IVideoPimResponsive;
  products_url: string;
  products_count: number;
}
export interface IVirtualCategory {
  id: number;
  slug: string;
  title: string;
  description: string;
  content?: Record<string, Record<string, unknown>>;
  filters: Record<string, string[] | string>;
  sort: string;
  meta: {
    title: string;
    description: string;
  };
}
export type IVirtualCategoryFilters = Record<string, string[] | string>;
export interface INavigationVirtualCategory {
  slug: string;
  title: string;
}
export interface IProductObject {
  id: number;
  index: number;
  uuid: string;
  files: IProductFileObject[];
  flags: TProductFlagObject[];
  manufacturer: IProductManufacturerObject;
  name: string;
  slug: string;
  colors: IProductColor[];
  getPrice: (priceListName: string) => string;
  getPromoPrice: (priceListName: string) => string;
  getPromoPercent: (priceListName: string) => string;
  getOmnibusPrice: (priceListName: string) => string;
  getFromFlag: (priceListName: string) => boolean;
  checkFlagActive: (flagName: TProductFlagsKey) => boolean;
  swatches: ISwatch[];
  packshots: IProductPackshots;
  configurable: boolean;
  behaviour?: string;
  colorSwatch: IProductColorSwatch;
  colorsCount: number;
  priceUnit?: string;
  gpsr?: IGpsrObject;
}
export type TAlgoliaProductHit = {
  name_pl: string;
  name_es: string;
  name_en: string;
  name_de: string;
  id_product: number;
  collection: Record<string, unknown>;
  manufacturer: IProductManufacturerObject;
  designers: Array<Record<string, unknown>>;
  image: string;
  category: Record<string, unknown>;
  publish_state: string;
  files: IProductFileObject[];
  flags: TProductFlagObject[];
  configurable: boolean;
  behaviour?: string | {
    value: string;
  };
  colorSwatch: IProductColorSwatch;
  basePrice: IProductPriceObject;
  currentPrice: IProductPriceObject;
  omnibusPrice: IProductPriceObject;
  price: IProductPriceObject;
  priceUnit: string;
  orderUnit: string;
};
export interface IAlgoliaIdeaHitProduct {
  id: number;
  name: string;
  shortName: string | null;
  slug: string;
}
export type TAlgoliaIdeaHit = {
  objectID: string;
  slug: string;
  localizedSlug: string;
  id: number;
  parentId: number | null;
  projectUrl: string | null;
  name: string;
  description: string;
  image: string;
  tags: Record<string, string>;
  products: IAlgoliaIdeaHitProduct[];
};
export type TAlgoliaProjectHit = {
  objectID: string;
  name: string;
  slug: string;
  description: string;
  localized_slug: string;
  id: number;
  path: string;
  document_url: string;
  children_url: string;
  class: string;
  position: number;
  image: string;
  surface: string;
  project_url: string;
  tags: Record<string, string>;
  products: IAlgoliaIdeaHitProduct[];
  furniture: string[];
  doors: string[];
  walls: string[];
  floor: string[];
};
export type TAlgoliaShopHit = {
  objectID: string;
  name: Record<string, string>;
  uuid: string;
  ids: {
    'VOX-OLD-ID-BAZA-SALONY': string;
    'INTERNAL-PIM-ID': string;
  };
  vendor: {
    code: string;
    name: Record<string, string>;
  };
  group: {
    code: string;
    name: Record<string, string>;
    listingName: Record<string, string>;
  };
  country: {
    code: string;
    name: Record<string, string>;
  };
  timezone: string;
  location: ILocation;
  openingHours: {
    description: Record<string, string>;
    days: Array<{
      dayOfWeek: string;
      opens: string;
      closes: string;
    }>;
  };
  address: {
    city: Record<string, string>;
    street: string;
    state: Record<string, string>;
    postalCode: string;
    mallName: string;
    locationHint?: unknown;
  };
  description: {
    summary: Record<string, string>;
    offer: Record<string, string>;
  };
  gallery: IPointOfXGallery;
  offer: unknown;
  flags: unknown[];
  contact: IPointOfDeliveryContact;
};
export type THitProduct = AlgoliaHit<TAlgoliaProductHit>;
export type THitIdea = AlgoliaHit<TAlgoliaIdeaHit>;
export type THitProject = AlgoliaHit<TAlgoliaProjectHit>;
export type THitShop = AlgoliaHit<TAlgoliaShopHit>;
export type TActiveTab = 'all' | 'products' | 'ideas' | 'interiorDesign' | 'shop';
export interface ICategoriesMobileBottomPanelProps {
  isMobile?: boolean;
  productsCount: number;
  filterToggle: () => void;
  onFilterButtonClick: (args: string) => void;
  listingRef: RefObject<HTMLDivElement | null>;
}
export enum BehaviourType {
  CATEGORY = 'category',
  COLLECTION = 'collection',
  ROOM = 'room',
  COLUMN_COLLECTION = 'column_collection',
  COLUMN_BRAND = 'column_brand',
  COLUMN_TYPE = 'column_type',
  HIDDEN = 'hidden',
  EXTERNAL_LINK = 'external_link',
}
export type TCategoryMapBehaviour = 'category' | 'collection' | 'collectionContent';
export type TPublicationFlags = 'test' | 'prod';
export interface ICategoryMapObject {
  id: number;
  slug: string;
  behaviour: TCategoryMapBehaviour;
  virtualCategoriesSlugs?: string[];
}
export interface ICategoryMap {
  id: number;
  slug: string;
  subpages: Array<ICategoryMapObject>;
  flags: Array<TPublicationFlags>;
}
export interface ICategory {
  id: number;
  parent_id?: number | null;
  name: string;
  slug: string;
  behaviour?: BehaviourType;
  products_url?: string;
  category_url?: string;
  files?: TImage[];
  children: Record<symbol, ICategory>;
  categoryPage?: ICategoryMap;
  infoCards?: IInfoCardWithMedia[];
  pageSlug: string;
  cms?: {
    informationBlocks?: unknown[];
    categoryPage?: unknown;
    information_blocks?: unknown[];
    category_page?: unknown;
  };
  position: number;
  seo?: unknown;
  is_new?: boolean;
  personalization?: boolean;
  promo?: boolean;
  extra_buttons?: {
    buttons: {
      link: ICmsLink;
      textColor: string | null;
    }[];
    label_text: string | null;
    label_text_color: string | null;
  };
  external_link?: string;
  arrangement_products?: IArrangementProduct[];
  hide_from_listing_thumbnail?: boolean;
  virtual_categories?: IVirtualCategory[];
}
export type TCategoryArray = Omit<ICategory, 'children'> & {
  children: TCategoryArray[];
};
export type TFilterAbstract = {
  type: string;
  name: string;
  code: string;
};
export type TFilterSelectOptions = {
  [key: string]: {
    name: string;
    featureImageUrl?: string;
    featureValueDescriptive?: string;
  };
};
export interface IFilterOption {
  name: string;
  value: string;
  image?: string;
}
export type TFilterDetail = TFilterAbstract & {
  options?: boolean[] | IFilterOption[];
};
export type TPagination = {
  limit: number;
  offset: number;
  count: number;
};
export type TPaginationState = {
  productsToRender: number;
  offset: number;
  count: number;
};
export interface ISortingObject {
  // needs fix on pimcore side
  available: Array<Array<string>>;
  current: string;
  order: string;
}
export interface IListingData {
  filters: Record<string, unknown> | null;
  pagination: TPagination;
  products: IProductObject[];
  sorting: ISortingObject | null;
}
export interface IListingDataResponse {
  data: IListingData;
}
export enum ECategoryFile {
  mainImage = 'CATEGORY_MAIN',
  thumbnailImage = 'CATEGORY_THUMBNAIL',
  menuImage = 'CATEGORY_MENU',
  searchIcon = 'CATEGORY_SEARCH_ICON',
}
export interface IParams extends ParsedUrlQuery {
  slug: string;
  mainCategory: string;
}
export type TParsedImageWithType = IParsedImage & {
  type: ECategoryFile;
};
export type TCategoryDataFiles = Array<TImage & IParsedImage>;
export interface ICategorySeo {
  title: string;
  description: string;
  imagePaths?: string[];
}
export interface ICategoryData {
  id: number;
  name: string;
  files: TCategoryDataFiles;
  seo: ICategorySeo;
  content?: Record<string, Record<string, unknown>>;
}
export interface ICategoryDataResponse {
  data: ICategoryData;
}
export type ICategoryScreenRender<T> = Record<Required<TResponsiveType>, T>;
export type IFlatMarketingCategoryObject = Record<string, ICategory>;
export type TCategoriesTree = Record<symbol, ICategory>;
export type TServiceMap = Record<symbol, string>;
export interface IGpsrObject {
  energyClassCode: string | null;
  energyClassIcon: string | null;
  eprelNumber: string;
  eprelType: string | null;
  informationLabelUrl: string | null;
  informationPageUrl: string | null;
  informationSheetUrl: string | null;
}